import { supabase } from '../supabaseClient';

// Send a friend request (or re-request after a decline — deletes old row first)
export const sendFriendRequest = async (fromUser, toUser) => {
  // Delete any previous declined request so UNIQUE constraint doesn't block re-request
  await supabase
    .from('friendships')
    .delete()
    .eq('requester', fromUser)
    .eq('recipient', toUser)
    .eq('status', 'declined');

  const { error } = await supabase
    .from('friendships')
    .insert({ requester: fromUser, recipient: toUser });
  if (error) throw error;
};

// Accept a pending friend request
export const acceptFriendRequest = async (friendshipId) => {
  const { error } = await supabase
    .from('friendships')
    .update({ status: 'accepted', updated_at: new Date().toISOString() })
    .eq('id', friendshipId);
  if (error) throw error;
};

// Decline a pending friend request
export const declineFriendRequest = async (friendshipId) => {
  const { error } = await supabase
    .from('friendships')
    .update({ status: 'declined', updated_at: new Date().toISOString() })
    .eq('id', friendshipId);
  if (error) throw error;
};

// Remove a friend (unfriend)
export const removeFriend = async (friendshipId) => {
  const { error } = await supabase
    .from('friendships')
    .delete()
    .eq('id', friendshipId);
  if (error) throw error;
};

// Get all accepted friends for a user
export const getFriends = async (username) => {
  const { data, error } = await supabase
    .from('friendships')
    .select('id, requester, recipient, status')
    .or(`requester.eq.${username},recipient.eq.${username}`)
    .eq('status', 'accepted');

  if (error) throw error;

  return (data || []).map(f => {
    const friendName = f.requester === username ? f.recipient : f.requester;
    return { friendshipId: f.id, username: friendName };
  });
};

// Get pending incoming requests for a user
export const getPendingRequests = async (username) => {
  const { data, error } = await supabase
    .from('friendships')
    .select('id, requester, created_at')
    .eq('recipient', username)
    .eq('status', 'pending');

  if (error) throw error;
  return data || [];
};

// Get the friendship status between two users (for ProfileModal)
export const getFriendshipStatus = async (currentUser, otherUser) => {
  const { data, error } = await supabase
    .from('friendships')
    .select('id, requester, recipient, status')
    .or(
      `and(requester.eq.${currentUser},recipient.eq.${otherUser}),and(requester.eq.${otherUser},recipient.eq.${currentUser})`
    )
    .limit(1);

  if (error) throw error;
  if (!data || data.length === 0) return { status: 'none', friendshipId: null };

  const row = data[0];
  return {
    status: row.status,
    friendshipId: row.id,
    iAmRequester: row.requester === currentUser,
    iAmRecipient: row.recipient === currentUser,
  };
};

// Subscribe to friendship changes for a user (real-time)
export const subscribeToFriendships = (username, callback) => {
  const channel = supabase
    .channel(`friendships:${username}`)
    .on('postgres_changes',
      { event: '*', schema: 'public', table: 'friendships', filter: `requester=eq.${username}` },
      callback
    )
    .on('postgres_changes',
      { event: '*', schema: 'public', table: 'friendships', filter: `recipient=eq.${username}` },
      callback
    )
    .subscribe();

  return () => channel.unsubscribe();
};
